/**
 * Stars Service
 *
 * Handles all API calls related to the stars system.
 * Provides methods for toggling stars, fetching star data,
 * and retrieving leaderboards.
 */

const API_URL = process.env.VUE_APP_API_URL;

/**
 * Get authentication headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth-token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Toggle star for a place or article
 *
 * @param {string} itemId - Place slug or article ObjectId
 * @param {string} itemType - 'place' or 'article'
 * @returns {Promise<Object>} - Star operation result
 */
export const toggleStar = async (itemId, itemType) => {
  try {
    const response = await fetch(`${API_URL}/api/stars`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to toggle star');
    }

    return data;
  } catch (error) {
    console.error('Error toggling star:', error);
    throw error;
  }
};

/**
 * Get star data for a place
 *
 * @param {string} placeId - Place slug
 * @returns {Promise<Object>} - Star count and user status
 */
export const getPlaceStars = async (placeId) => {
  try {
    const response = await fetch(
      `${API_URL}/api/places/${placeId}/stars`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch place stars');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching place stars:', error);
    throw error;
  }
};

/**
 * Get star data for an article
 *
 * @param {string} articleId - Article ObjectId
 * @returns {Promise<Object>} - Star count and user status
 */
export const getArticleStars = async (articleId) => {
  try {
    const response = await fetch(
      `${API_URL}/api/articles/${articleId}/stars`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch article stars');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching article stars:', error);
    throw error;
  }
};

/**
 * Get user's starred items
 *
 * @param {string} userId - User ID (use 'me' for current user)
 * @param {Object} options - Query options
 * @param {string} options.type - Filter by 'place' or 'article'
 * @param {number} options.limit - Number of results
 * @param {number} options.skip - Pagination offset
 * @returns {Promise<Object>} - User's starred items
 */
export const getUserStars = async (userId = 'me', options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.type) params.append('type', options.type);
    if (options.limit) params.append('limit', options.limit);
    if (options.skip) params.append('skip', options.skip);

    const queryString = params.toString();
    const url = `${API_URL}/api/users/${userId}/stars${queryString ? '?' + queryString : ''}`;

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch user stars');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching user stars:', error);
    throw error;
  }
};

/**
 * Get star leaderboards
 *
 * @param {Object} options - Query options
 * @param {string} options.type - 'places', 'articles', or 'users'
 * @param {string} options.country - ISO country code
 * @param {string} options.category - Category slug
 * @param {number} options.limit - Number of results
 * @returns {Promise<Object>} - Leaderboard data
 */
export const getStarLeaderboards = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.type) params.append('type', options.type);
    if (options.country) params.append('country', options.country);
    if (options.category) params.append('category', options.category);
    if (options.limit) params.append('limit', options.limit);

    const queryString = params.toString();
    const url = `${API_URL}/api/stars/leaderboards${queryString ? '?' + queryString : ''}`;

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch leaderboards');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching star leaderboards:', error);
    throw error;
  }
};

export default {
  toggleStar,
  getPlaceStars,
  getArticleStars,
  getUserStars,
  getStarLeaderboards
};
